import React, { useState, useRef, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Icon, useToast,
  InputGroup, InputLeftAddon, Divider, RadioGroup, Radio, Stack,
  Link,
} from '@chakra-ui/react';
import { FiFile, FiDownload } from 'react-icons/fi';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import axiosInstance from '../../../api/AxiosInstance';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
  onCerrarEvento?: () => void;
}

const TIPOS_EVENTO = ['Feria', 'Festival', 'Ceremonia', 'Exposición', 'Presentación', 'Otro'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const FormularioEvento: React.FC<Props> = ({ modo, data, onGuardado, onCancelar, onCerrarEvento }) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tipoRegistro, setTipoRegistro] = useState(data?.tipoRegistro || 'NUEVO');
  const [titulo, setTitulo] = useState(data?.titulo || '');
  const [tipoEvento, setTipoEvento] = useState(data?.tipoEvento || '');
  const [concepto, setConcepto] = useState(data?.concepto || '');
  const [ubicacion, setUbicacion] = useState(data?.ubicacion || '');
  const [comunidad, setComunidad] = useState(data?.comunidad || '');
  const [fechaEvento, setFechaEvento] = useState(
    data?.fechaEvento ? data.fechaEvento.split('T')[0] : ''
  );
  const [horaInicio, setHoraInicio] = useState(data?.horaInicio || '');
  const [horaFin, setHoraFin] = useState(data?.horaFin || '');
  const [itinerario, setItinerario] = useState(data?.itinerario || '');
  const [funcionarioAsiste, setFuncionarioAsiste] = useState(data?.funcionarioAsiste || '');
  const [cargo, setCargo] = useState(data?.cargo || '');
  const [artistas, setArtistas] = useState(data?.artistas || '');
  const [invitados, setInvitados] = useState(data?.invitados || '');
  const [personalRequerido, setPersonalRequerido] = useState(data?.personalRequerido || '');
  const [presupuesto, setPresupuesto] = useState(
    data?.presupuestoAsignado != null ? String(data.presupuestoAsignado) : ''
  );
  const [registroCompras, setRegistroCompras] = useState(data?.registroCompras || '');
  const [observacionesTesoreria, setObservacionesTesoreria] = useState(data?.observacionesTesoreria || '');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [cotizaciones, setCotizaciones] = useState<File[]>([]);
  const [localidades, setLocalidades] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axiosInstance
      .get('/cLocalidadRoutes')
      .then((res) => setLocalidades(res.data || []))
      .catch(() => setLocalidades([]));
  }, []);

  const handleSeleccionarCotizaciones = () => fileInputRef.current?.click();

  const handleCambioArchivos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const pdfs = files.filter((f) => f.type === 'application/pdf');
    if (pdfs.length < files.length) {
      toast({ title: 'Solo se permiten PDFs', status: 'warning', duration: 3000 });
    }
    setCotizaciones((prev) => [...prev, ...pdfs].slice(0, 5));
  };

  const handleGuardar = async () => {
    if (!titulo || !tipoEvento || !ubicacion || !comunidad || !fechaEvento || !responsable) {
      toast({ title: 'Campos obligatorios', description: 'Completa los campos requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const formData = new FormData();
      formData.append('tipoRegistro', tipoRegistro);
      formData.append('titulo', titulo);
      formData.append('tipoEvento', tipoEvento);
      formData.append('concepto', concepto);
      formData.append('ubicacion', ubicacion);
      formData.append('comunidad', comunidad);
      formData.append('fechaEvento', fechaEvento);
      formData.append('horaInicio', horaInicio);
      formData.append('horaFin', horaFin);
      formData.append('itinerario', itinerario);
      formData.append('funcionarioAsiste', funcionarioAsiste);
      formData.append('cargo', cargo);
      formData.append('artistas', artistas);
      formData.append('invitados', invitados);
      formData.append('personalRequerido', personalRequerido);
      formData.append('presupuestoAsignado', presupuesto);
      formData.append('registroCompras', registroCompras);
      formData.append('observacionesTesoreria', observacionesTesoreria);
      formData.append('responsable', responsable);
      cotizaciones.forEach((f) => formData.append('cotizaciones', f));

      if (modo === 'edit') {
        await axiosInstance.put(`/cultura/eventos/${data.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axiosInstance.post('/cultura/eventos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast({
        title: modo === 'edit' ? 'Evento actualizado' : 'Evento registrado',
        status: 'success',
        duration: 3000,
      });
      onGuardado?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Error al guardar el evento.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarFicha = () => {
    const d = data || {};
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

    doc.setFillColor(161, 38, 61);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('FICHA DE EVENTO CULTURAL', 105, 14, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let y = 30;

    const addSection = (title: string) => {
      doc.setFillColor(245, 245, 245);
      doc.rect(14, y - 4, 182, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(161, 38, 61);
      doc.text(title.toUpperCase(), 16, y);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      y += 6;
    };

    const addField = (label: string, value: string, col = 0, total = 1) => {
      const w = 182 / total;
      const x = 14 + col * w;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(label + ':', x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value || '—', x + 35, y);
      if (col === total - 1) y += 6;
    };

    addSection('Información General');
    addField('Tipo Registro', d.tipoRegistro, 0, 2);
    addField('Estatus', d.estatus, 1, 2);
    y += 0;
    addField('Título', d.titulo, 0, 1);
    addField('Tipo de Evento', d.tipoEvento, 0, 2);
    addField('Responsable', d.responsable, 1, 2);
    y += 0;
    addField('Fecha', d.fechaEvento ? new Date(d.fechaEvento).toLocaleDateString('es-MX') : '—', 0, 2);
    addField('Horario', `${d.horaInicio || '—'} - ${d.horaFin || '—'}`, 1, 2);
    y += 0;
    addField('Comunidad', d.comunidad, 0, 2);
    addField('Ubicación', d.ubicacion, 1, 2);
    y += 4;

    if (d.concepto) {
      addSection('Concepto / Descripción');
      const lines = doc.splitTextToSize(d.concepto, 178);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 4;
    }

    if (d.itinerario) {
      addSection('Itinerario');
      const lines = doc.splitTextToSize(d.itinerario, 178);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 4;
    }

    addSection('Participantes');
    addField('Funcionario', d.funcionarioAsiste, 0, 2);
    addField('Cargo', d.cargo, 1, 2);
    y += 0;
    addField('Artistas', d.artistas, 0, 1);
    addField('Invitados', d.invitados, 0, 1);
    addField('Personal req.', d.personalRequerido, 0, 1);
    y += 4;

    addSection('Información Financiera');
    addField('Presupuesto', d.presupuestoAsignado != null ? `$${Number(d.presupuestoAsignado).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—', 0, 1);
    if (d.registroCompras) {
      addField('Registro de compras', d.registroCompras, 0, 1);
    }
    if (d.observacionesTesoreria) {
      addField('Obs. Tesorería', d.observacionesTesoreria, 0, 1);
    }
    y += 4;

    if (d.observacionesFinales) {
      addSection('Observaciones Finales');
      const lines = doc.splitTextToSize(d.observacionesFinales, 178);
      doc.text(lines, 14, y);
      y += lines.length * 5 + 4;
    }

    const cotizacionesArchivos = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'cotizacion');
    const fotosArchivos = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'foto');
    const evidenciasArchivos = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'evidencia_pdf');

    if (cotizacionesArchivos.length > 0 || fotosArchivos.length > 0 || evidenciasArchivos.length > 0) {
      addSection('Archivos');
      [...cotizacionesArchivos, ...evidenciasArchivos].forEach((a: any) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 200);
        doc.textWithLink(`• [${a.tipoArchivo}] ${a.nombreArchivo}`, 16, y, { url: `${API}${a.rutaArchivo}` });
        doc.setTextColor(0, 0, 0);
        y += 5;
      });
    }

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generado: ${new Date().toLocaleString('es-MX')}`, 14, 288);

    doc.save(`Ficha_Evento_${d.titulo?.replace(/\s+/g, '_') || d.id}.pdf`);
  };

  const fmt = (v: string) =>
    v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

  // ========== MODO VISTA ==========
  if (modo === 'view') {
    const d = data || {};
    const cotizacionesA = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'cotizacion');
    const fotosA = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'foto');
    const evidenciasA = (d.archivos || []).filter((a: any) => a.tipoArchivo === 'evidencia_pdf');

    return (
      <VStack spacing={3} align="stretch">
        <HStack justify="flex-end" spacing={2}>
          <Button size="sm" leftIcon={<FiDownload />} colorScheme="red" variant="outline" onClick={handleDescargarFicha}>
            Descargar ficha PDF
          </Button>
          {d.estatus === 'Activo' && onCerrarEvento && (
            <Button size="sm" colorScheme="blue" onClick={onCerrarEvento}>
              Cerrar evento
            </Button>
          )}
        </HStack>

        <SectionTitle>Información General</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="TIPO DE REGISTRO" value={<Badge colorScheme={d.tipoRegistro === 'PASADO' ? 'purple' : 'cyan'}>{d.tipoRegistro}</Badge>} />
          <Field label="ESTATUS" value={<Badge colorScheme={d.estatus === 'Activo' ? 'green' : 'blue'}>{d.estatus}</Badge>} />
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="TÍTULO" value={d.titulo} />
          </Box>
          <Field label="TIPO DE EVENTO" value={d.tipoEvento} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="FECHA DEL EVENTO" value={fmt(d.fechaEvento)} />
          <Field label="HORARIO" value={d.horaInicio ? `${d.horaInicio} — ${d.horaFin || '?'}` : undefined} />
          <Field label="COMUNIDAD" value={d.comunidad} />
          <Field label="UBICACIÓN" value={d.ubicacion} />
        </SimpleGrid>

        {d.concepto && (
          <>
            <SectionTitle>Concepto / Descripción</SectionTitle>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.concepto}</Text>
          </>
        )}

        {d.itinerario && (
          <>
            <SectionTitle>Itinerario</SectionTitle>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.itinerario}</Text>
          </>
        )}

        <SectionTitle>Participantes</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FUNCIONARIO QUE ASISTE" value={d.funcionarioAsiste} />
          <Field label="CARGO" value={d.cargo} />
          <Field label="ARTISTAS" value={d.artistas} />
          <Field label="INVITADOS" value={d.invitados} />
          <Box gridColumn={{ md: 'span 2' }}>
            <Field label="PERSONAL REQUERIDO" value={d.personalRequerido} />
          </Box>
        </SimpleGrid>

        <SectionTitle>Presupuesto e Información Financiera</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="PRESUPUESTO ASIGNADO" value={d.presupuestoAsignado != null ? `$${Number(d.presupuestoAsignado).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : undefined} />
          <Box />
          {d.registroCompras && (
            <Box gridColumn={{ md: 'span 2' }}>
              <Field label="REGISTRO DE COMPRAS/INSUMOS" value={d.registroCompras} />
            </Box>
          )}
          {d.observacionesTesoreria && (
            <Box gridColumn={{ md: 'span 2' }}>
              <Field label="OBSERVACIONES PARA TESORERÍA" value={d.observacionesTesoreria} />
            </Box>
          )}
        </SimpleGrid>

        {cotizacionesA.length > 0 && (
          <>
            <SectionTitle>Cotizaciones</SectionTitle>
            <VStack align="start" spacing={1}>
              {cotizacionesA.map((a: any) => (
                <HStack key={a.id}>
                  <Icon as={FiFile} color="red.500" />
                  <Link color="blue.500" href={`${API}${a.rutaArchivo}`} isExternal textDecoration="underline" fontSize="sm">
                    {a.nombreArchivo}
                  </Link>
                </HStack>
              ))}
            </VStack>
          </>
        )}

        {(fotosA.length > 0 || evidenciasA.length > 0 || d.observacionesFinales || d.listadoAsistentes) && (
          <>
            <Divider />
            <SectionTitle>Evidencias del Evento</SectionTitle>
          </>
        )}

        {fotosA.length > 0 && (
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>FOTOGRAFÍAS</Text>
            <HStack spacing={2} flexWrap="wrap">
              {fotosA.map((a: any) => (
                <Box key={a.id} as="a" href={`${API}${a.rutaArchivo}`} target="_blank">
                  <Box
                    w="80px" h="80px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200"
                    bg="gray.100" display="flex" alignItems="center" justifyContent="center"
                  >
                    <img src={`${API}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                </Box>
              ))}
            </HStack>
          </Box>
        )}

        {evidenciasA.length > 0 && (
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>PDFs DE EVIDENCIA</Text>
            <VStack align="start" spacing={1}>
              {evidenciasA.map((a: any) => (
                <HStack key={a.id}>
                  <Icon as={FiFile} color="red.500" />
                  <Link color="blue.500" href={`${API}${a.rutaArchivo}`} isExternal textDecoration="underline" fontSize="sm">
                    {a.nombreArchivo}
                  </Link>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {d.listadoAsistentes && (
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">LISTADO OFICIAL DE ASISTENTES</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.listadoAsistentes}</Text>
          </Box>
        )}

        {d.observacionesFinales && (
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">OBSERVACIONES FINALES</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.observacionesFinales}</Text>
          </Box>
        )}
      </VStack>
    );
  }

  // ========== MODO NUEVO / EDITAR ==========
  return (
    <VStack spacing={3} align="stretch">
      {/* Selección tipo de registro (solo en modo nuevo) */}
      {modo === 'new' && (
        <Box>
          <SectionTitle>Tipo de registro</SectionTitle>
          <RadioGroup value={tipoRegistro} onChange={setTipoRegistro} mt={2}>
            <Stack direction="row" spacing={6}>
              <Radio value="NUEVO" colorScheme="teal" size="lg">
                <Box>
                  <Text fontWeight="bold">NUEVO</Text>
                  <Text fontSize="xs" color="gray.500">Evento próximo a realizarse</Text>
                </Box>
              </Radio>
              <Radio value="PASADO" colorScheme="purple" size="lg">
                <Box>
                  <Text fontWeight="bold">PASADO</Text>
                  <Text fontSize="xs" color="gray.500">Evento ya realizado (registro histórico)</Text>
                </Box>
              </Radio>
            </Stack>
          </RadioGroup>
        </Box>
      )}

      {/* Información básica */}
      <SectionTitle>Información del Evento</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Título del evento</FormLabel>
          <Input placeholder="Ej. Festival de las Artes 2026" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo de evento</FormLabel>
          <Select placeholder="Selecciona tipo" value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)}>
            {TIPOS_EVENTO.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input placeholder="Nombre del responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha del evento</FormLabel>
          <Input type="date" value={fechaEvento} onChange={(e) => setFechaEvento(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Hora inicio</FormLabel>
          <Input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Hora fin</FormLabel>
          <Input type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Comunidad</FormLabel>
          <Select placeholder="Selecciona comunidad" value={comunidad} onChange={(e) => setComunidad(e.target.value)}>
            {localidades.map((loc) => (
              <option key={loc.id} value={loc.descripcion}>{loc.descripcion}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Ubicación</FormLabel>
          <Input placeholder="Lugar específico del evento" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      {/* Descripción */}
      <SectionTitle>Descripción</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Concepto / Descripción</FormLabel>
          <Textarea rows={3} placeholder="Descripción general del evento..." value={concepto} onChange={(e) => setConcepto(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Itinerario</FormLabel>
          <Textarea rows={5} placeholder="Detalla las actividades, horarios y secuencia del evento..." value={itinerario} onChange={(e) => setItinerario(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      {/* Participantes */}
      <SectionTitle>Participantes</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Funcionario que asiste</FormLabel>
          <Input placeholder="Nombre del funcionario" value={funcionarioAsiste} onChange={(e) => setFuncionarioAsiste(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Cargo</FormLabel>
          <Input placeholder="Cargo del funcionario" value={cargo} onChange={(e) => setCargo(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Artistas</FormLabel>
          <Input placeholder="Nombres de artistas participantes" value={artistas} onChange={(e) => setArtistas(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Invitados</FormLabel>
          <Input placeholder="Invitados especiales" value={invitados} onChange={(e) => setInvitados(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Personal requerido</FormLabel>
          <Input placeholder="Ej. 5 voluntarios, 2 técnicos de sonido..." value={personalRequerido} onChange={(e) => setPersonalRequerido(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      {/* Presupuesto */}
      <SectionTitle>Presupuesto e Información Financiera</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Presupuesto asignado</FormLabel>
          <InputGroup>
            <InputLeftAddon>$</InputLeftAddon>
            <Input type="number" min={0} placeholder="0.00" value={presupuesto} onChange={(e) => setPresupuesto(e.target.value)} />
          </InputGroup>
        </FormControl>
        <Box />
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Registro de compras / insumos</FormLabel>
          <Textarea rows={3} placeholder="Detalla los insumos y compras necesarios..." value={registroCompras} onChange={(e) => setRegistroCompras(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones para Tesorería</FormLabel>
          <Textarea rows={2} placeholder="Notas o requerimientos especiales para Tesorería..." value={observacionesTesoreria} onChange={(e) => setObservacionesTesoreria(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      {/* Cotizaciones */}
      <SectionTitle>Cotizaciones / Documentos (PDFs)</SectionTitle>
      <input ref={fileInputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }} onChange={handleCambioArchivos} />

      {/* Archivos existentes (modo editar) */}
      {modo === 'edit' && data?.archivos?.filter((a: any) => a.tipoArchivo === 'cotizacion').length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Cotizaciones actuales:</Text>
          {data.archivos.filter((a: any) => a.tipoArchivo === 'cotizacion').map((a: any) => (
            <HStack key={a.id}>
              <Icon as={FiFile} color="red.500" />
              <Text fontSize="sm" color="gray.600">{a.nombreArchivo}</Text>
            </HStack>
          ))}
        </Box>
      )}

      <Box
        border="2px dashed" borderColor="gray.300" borderRadius="md" p={4} textAlign="center"
        cursor="pointer" _hover={{ borderColor: '#5b182b', bg: 'red.50' }}
        onClick={handleSeleccionarCotizaciones}
      >
        {cotizaciones.length > 0 ? (
          <VStack spacing={1}>
            {cotizaciones.map((f, i) => (
              <HStack key={i}>
                <Icon as={FiFile} color="red.500" />
                <Text fontSize="sm" color="gray.700">{f.name}</Text>
              </HStack>
            ))}
            <Text fontSize="xs" color="gray.400">Haz clic para agregar más (máx. 5)</Text>
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500">Haz clic para seleccionar PDFs de cotizaciones (máx. 5)</Text>
        )}
      </Box>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioEvento;
